import type { RouteLocationNormalized } from 'vue-router'
import type { InjectionKey, Ref } from 'vue'

import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { getLikedBookSlugsFromUserChoices } from '@/models/onboarding.personalization'
import type { QuestionnaireUserChoices } from '@/models/onboarding.types'
import { backend } from '@/services/backend'

/**
 * Return the book url_slug when the route is under /app/book...
 * Used to set ?source_book= when navigating to billing.
 */
export function extractDocSlugFromRoute(
  route: RouteLocationNormalized,
): string | null {
  if (!route.path.startsWith('/app/book/')) {
    return null
  }
  return (route.params.url_slug as string) || null
}

/**
 * Parse a book url_slug from sf_first_page summary/pdf URLs.
 * Same path shape signup uses for first_doc_url_slug.
 */
export function bookSlugFromFirstPage(firstPage: string | null): string | null {
  const match = firstPage?.match(/\/(summary|pdf)\/([^/?#]+)/)
  return match ? match[2].split(`-${match[1]}-`)[0] : null
}

/**
 * Read onboarding questionnaire choices from local storage, if present.
 */
export function questionnaireChoicesFromStorage(): QuestionnaireUserChoices | null {
  const raw = storage.getItem(StorageKeys.questionnaireChoices)
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as QuestionnaireUserChoices
  } catch {
    return null
  }
}

/**
 * Return the book slug the user's session originated from (summary or PDF
 * page), or null if there is no known content origin.
 *
 * This is a thin wrapper around bookSlugFromFirstPage() that we use in the onboarding
 * flow, before we have questionnaire book choices, and it returns a "real" null if
 * we don't know the content origin, rather than falling back to a default book slug.
 */
export function getKnownContentOriginSlug(): string | null {
  return bookSlugFromFirstPage(storage.getItem(StorageKeys.firstPage))
}

/**
 * Resolve interest book slug:
 * source_book > first liked onboarding book > sf_first_page > atomic-habits.
 */
export function resolveInterestBookSlug(
  sourceBook: string | null | undefined,
  choices: QuestionnaireUserChoices | null | undefined,
  firstPage: string | null | undefined,
): string {
  if (sourceBook) {
    return sourceBook
  }
  if (choices) {
    const liked = getLikedBookSlugsFromUserChoices(choices)
    if (liked[0]) {
      return liked[0]
    }
  }
  return bookSlugFromFirstPage(firstPage ?? null) ?? 'atomic-habits'
}

export type InterestBook = {
  url_slug: string
  title: string
  author: string
  cover_image?: string | null
  tags: string[]
}

/**
 * Injection key for the billing interest book (checkout modal personalization).
 * Provided by Billing.vue; injected by SubscribePopup.
 */
export const interestBookKey: InjectionKey<Ref<InterestBook | null>> =
  Symbol('interestBook')

/**
 * Resolve the interest book and load metadata via GET /books/{slug}.
 */
export async function loadInterestBook(
  sourceBook?: string | null,
): Promise<InterestBook> {
  const choices = sourceBook ? null : questionnaireChoicesFromStorage()
  const slug = resolveInterestBookSlug(
    sourceBook,
    choices,
    storage.getItem(StorageKeys.firstPage),
  )
  const book = await backend.getBook(slug)
  return {
    url_slug: book.url_slug,
    title: book.title,
    author: book.author,
    cover_image: book.cover_image,
    tags: book.tags ?? [],
  }
}
